import { useState } from 'react';
import { MdSend, MdClose, MdEdit, MdDelete, MdContentCopy } from 'react-icons/md';

const EditInterface = ({
  selectedText,
  onEdit,
  onClose,
  isProcessing,
  onDelete,
  onCopy
}) => {
  const [prompt, setPrompt] = useState('');
  const [showEditForm, setShowEditForm] = useState(false);

  const handleSubmit = () => {
    if (prompt.trim()) {
      onEdit(prompt);
      setPrompt('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleCopy = () => {
    if (onCopy) {
      onCopy(selectedText);
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete();
    }
  };

  return (
    <div className="inline-edit-interface bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-500 rounded-r-lg p-3 my-1 shadow-md animate-fadeIn">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          <MdEdit className="text-amber-600" size={16} />
          <h4 className="text-xs font-semibold text-amber-900">Selected Text Actions</h4>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <MdClose size={16} />
        </button>
      </div>

      {/* Selected Text Preview */}
      <div className="mb-3">
        <div className="flex items-center space-x-1 mb-1">
          <div className="w-0.5 h-3 bg-gray-400 rounded"></div>
          <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Selected Text</span>
        </div>
        <div className="bg-white/80 border border-gray-200 rounded p-2 max-h-24 overflow-y-auto">
          <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono leading-relaxed">
            {selectedText}
          </pre>
        </div>
      </div>

      {!showEditForm ? (
        /* Action Menu */
        <div className="flex flex-col space-y-2">
          <button
            onClick={() => setShowEditForm(true)}
            className="flex items-center space-x-2 px-3 py-2 bg-amber-600 text-white rounded hover:bg-amber-700 transition-colors duration-200 text-xs font-medium shadow-sm"
            disabled={isProcessing}
          >
            <MdEdit size={14} />
            <span>Edit with AI</span>
          </button>

          <button
            onClick={handleCopy}
            className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors duration-200 text-xs font-medium shadow-sm"
            disabled={isProcessing}
          >
            <MdContentCopy size={14} />
            <span>Copy to Clipboard</span>
          </button>

          <button
            onClick={handleDelete}
            className="flex items-center space-x-2 px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors duration-200 text-xs font-medium shadow-sm"
            disabled={isProcessing}
          >
            <MdDelete size={14} />
            <span>Delete Selection</span>
          </button>

          <button
            onClick={onClose}
            className="px-3 py-1.5 text-gray-600 hover:text-gray-800 transition-colors text-xs font-medium"
            disabled={isProcessing}
          >
            Cancel
          </button>
        </div>
      ) : (
        /* Edit Form */
        <>
          <div className="mb-2">
            <div className="flex items-center space-x-1 mb-1">
              <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">How should I edit this?</span>
            </div>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="E.g., 'make it more concise', 'add more details', 'fix grammar'..."
              className="w-full px-3 py-2 border border-amber-200 rounded text-xs resize-none focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 bg-white"
              rows={2}
              disabled={isProcessing}
              autoFocus
            />
          </div>

          <div className="flex items-center justify-end space-x-2">
            <button
              onClick={() => setShowEditForm(false)}
              className="px-3 py-1.5 text-gray-600 hover:text-gray-800 transition-colors text-xs font-medium"
              disabled={isProcessing}
            >
              Back
            </button>
            <button
              onClick={handleSubmit}
              disabled={isProcessing || !prompt.trim()}
              className="flex items-center space-x-1 px-3 py-1.5 bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 text-xs font-medium shadow-sm"
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin w-3 h-3 border-2 border-white border-t-transparent rounded-full"></div>
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <MdSend size={14} />
                  <span>Submit Edit</span>
                </>
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default EditInterface;
